import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import { Loader, ReloadIconButton, Title, TablePaginationWrapper } from '../../elements';
import { MedicalSuppliesService } from '../../hooks';
import {
  Avatar,
  Chip,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import { useStyles } from './styles';
import { CompleteMedicalSupplyOrderModal, OrderMedicalSupplyModal } from '../../components';
import {
  MedicalSupplyMethod,
  MedicalSupplyOrderItemViewModel,
  MedicalSupplyOrderStatus,
  MedicalSupplyOrderViewModel,
  MedicalSupplyType,
  VeterinaryOrderViewModel
} from '../../models/injection';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { AnimalType } from '../../models/animal';
import { renderSupplyRowLocations as renderSupplyLocationsCell } from '../../components/OrderMedicalSupplyModal/component';
import { useTranslation } from 'react-i18next';
import { List } from '@material-ui/core/';

const MedicalSupplyOrders: React.FC<any> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const medicalSuppliesService = new MedicalSuppliesService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [orderMedicalSupplyOpened, setOrderMedicalSupplyOpened] = React.useState(false);

  const [orders, setOrders] = React.useState([] as MedicalSupplyOrderViewModel[]);

  const loadMedicalSupplyOrders = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    medicalSuppliesService
      .getMedicalSupplyOrders(searchPage, rowsPerPage)
      .then((result) => {
        setOrders(result.data);
        setTotal(result.total);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadMedicalSupplyOrders();
    }
  }, [loaded]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const [selectedOrder, setSelectedOrder] = React.useState({} as MedicalSupplyOrderViewModel);
  const [completeOrderDialogOpen, setCompleteOrderDialogOpen] = React.useState(false);

  const handleCompleteOrder = (order: MedicalSupplyOrderViewModel) => {
    setSelectedOrder(order);

    order.items.forEach((item) => {
      item.delivered = true;
      item.realAmount = item.amount;
    });

    setCompleteOrderDialogOpen(true);
  };

  const handleCompleteMedicalSupplyOrder = (result: boolean | null) => {
    setCompleteOrderDialogOpen(false);

    if (result) {
      setLoaded(false);
    }
  };

  const handleOrderSupply = (result: boolean | null) => {
    setOrderMedicalSupplyOpened(false);

    if (result) {
      setLoaded(false);
    }
  };

  const renderActionCell = (order: MedicalSupplyOrderViewModel) => {
    if (order.status !== MedicalSupplyOrderStatus.Confirmed) {
      return null;
    }

    return (
      <Button color="primary" onClick={() => handleCompleteOrder(order)}>
        {t('f-supp-order-complete')}
      </Button>
    );
  };

  return (
    <>
      <Grid container direction="row" className={classes.paperHeader}>
        <Grid item container md={6} xs={10} spacing={1}>
          <Grid item>
            <Icon color="primary" fontSize="large">
              {'shopping_cart'}
            </Icon>
          </Grid>
          <Grid item>
            <Title>{t('f-supp-order-history')}</Title>
          </Grid>
          <Grid item>
            <Chip label={total} variant="outlined" />
          </Grid>
        </Grid>

        <Grid item container className={classes.orderHistoryHeaderRightSection} md={6} xs={12}>
          <Grid item>
            <ReloadIconButton action={() => setLoaded(false)} />
          </Grid>
          <Grid item>
            <Button id="createOrderBtn" color="primary" onClick={() => setOrderMedicalSupplyOpened(true)}>
              {t('f-supp-order-create')}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {/* data table */}
      {loaded && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TablePaginationWrapper
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            >
              <TableContainer component={Paper} elevation={1} className={classes.mt1}>
                <Table>
                  <TableBody>
                    <SupplyOrderTableHeader mode="farm" />

                    {orders.map((order, index) => (
                      <SupplyOrderTableRow
                        key={index}
                        order={order}
                        actionCellComponent={renderActionCell(order)}
                        mode="farm"
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TablePaginationWrapper>
          </Grid>
        </Grid>
      )}

      <OrderMedicalSupplyModal opened={orderMedicalSupplyOpened} onModalAction={handleOrderSupply} />

      <CompleteMedicalSupplyOrderModal
        opened={completeOrderDialogOpen}
        order={selectedOrder}
        onModalAction={handleCompleteMedicalSupplyOrder}
      />
    </>
  );
};

export default MedicalSupplyOrders;

export const SupplyOrderTableHeader = (props: { mode: 'farm' | 'veterinary' }) => {
  const { t } = useTranslation();
  const { mode } = props;

  return (
    <TableRow>
      <TableCell variant="head">{''}</TableCell>
      <TableCell variant="head">{t('f-search-number')}</TableCell>
      {mode === 'veterinary' && <TableCell variant="head">{t('f-com-farm')}</TableCell>}
      <TableCell variant="head">{t('f-supp-order-status')}</TableCell>
      <TableCell variant="head">{t('f-com-created')}</TableCell>
      <TableCell variant="head">{t('f-com-completed')}</TableCell>
      <TableCell variant="head">{''}</TableCell>
    </TableRow>
  );
};

// shared component, both for farm and vet view of the supply order table row;
export function SupplyOrderTableRow(props: {
  order: MedicalSupplyOrderViewModel | VeterinaryOrderViewModel;
  actionCellComponent?: React.ReactNode;
  mode: 'farm' | 'veterinary';
}) {
  const { order, actionCellComponent, mode } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const [rowExpanded, setRowExpanded] = React.useState(false);

  // details section (collapse) render
  const renderOrderDetailsSection = (order: MedicalSupplyOrderViewModel) => {
    return (
      <Grid container spacing={2}>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={4}>
            <Typography variant="body2">{t('f-supp-order-comments')}</Typography>
            <Typography variant="body1">{order.details?.comments || '-'}</Typography>
          </Grid>

          {order.confirmation && order.confirmation.dateTime && (
            <>
              <Grid item xs={4}>
                <Typography variant="body2">{t('f-supp-order-confirm-date')}</Typography>
                <Typography variant="body1">{new Date(order.confirmation.dateTime).toLocaleString()}</Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="body2">{t('f-vets')}</Typography>
                <Typography variant="body1">{`${order.confirmation.vetUserName} ${order.confirmation.vetUserUda}`}</Typography>
              </Grid>

              <Grid item xs={4}>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={order.details.successTreatment} />}
                  label={<Typography variant="body2">{t('f-supp-order-succ-treat')}</Typography>}
                />
              </Grid>

              <Grid item xs={4}>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={order.details.animalsVaccinability} />}
                  label={<Typography variant="body2">{t('f-supp-order-anim-vac')}</Typography>}
                />
              </Grid>

              <Grid item xs={4}>
                <FormControlLabel
                  control={<Checkbox color="primary" checked={order.details.successVaccination} />}
                  label={<Typography variant="body2">{t('f-supp-order-succ-vac')}</Typography>}
                />
              </Grid>
            </>
          )}

          {order.completed && (
            <Grid item xs={4}>
              <Typography variant="body2">{t('f-com-completed')}</Typography>
              <Typography variant="body1">{new Date(order.completed).toLocaleString()}</Typography>
            </Grid>
          )}
        </Grid>

        <Grid item>
          <Typography variant="overline">{t('f-supp-order-items')}</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell variant="head">{t('f-com-supply')}</TableCell>
                  <TableCell variant="head">{t('f-supp-order-locations')}</TableCell>
                  <TableCell variant="head">{t('f-com-lot')}</TableCell>
                  {/*<TableCell variant="head">{t('f-com-treat-num')}</TableCell>*/}
                  <TableCell variant="head">{t('f-com-reg-num')}</TableCell>
                  <TableCell variant="head">{t('f-com-withdrawal')}</TableCell>
                  <TableCell variant="head">{t('f-com-days-use')}</TableCell>
                  <TableCell variant="head" colSpan={2}>
                    {t('f-details')}
                  </TableCell>
                </TableRow>

                {order.items.map((item, index) => (
                  <TableRow key={item.id} className={classes.vAlignTop} hover>
                    <TableCell>{SupplyCell(item, order)}</TableCell>
                    <TableCell>{renderSupplyLocationsCell(item, index)}</TableCell>
                    <TableCell>{item.medicalSupplyBottle.lotNumber || '-'}</TableCell>
                    {/*<TableCell>*/}
                    {/*	{renderTreatmentCell(item)}*/}
                    {/*</TableCell>*/}
                    <TableCell>{item.medicalSupplyBottle.medicalSupply.registrationNumber || '-'}</TableCell>
                    <TableCell>
                      {t('f-com-number-days', { number: item.medicalSupplyBottle.medicalSupply.waitingTime }) || '-'}
                    </TableCell>
                    <TableCell style={{ padding: 0 }}>
                      <List dense={true}>
                        <ListItem className={classes.column} alignItems="flex-start">
                          <ListItemText
                            primary={t('f-com-number-days', { number: item.medicalSupplyBottle.daysToUse }) || '-'}
                          />
                        </ListItem>
                        <ListItem className={classes.column} alignItems="flex-start">
                          <ListItemText secondary={`${t('f-dose-per-animal')}:`} />
                          <ListItemText
                            primary={
                              item.medicalSupplyBottle.dosePerAnimal ? `${item.medicalSupplyBottle.dosePerAnimal}` : '-'
                            }
                          />
                        </ListItem>
                      </List>
                    </TableCell>
                    <TableCell style={{ padding: 0 }}>
                      <List dense={true}>
                        <ListItem className={classes.column} alignItems="flex-start">
                          <ListItemText secondary={`${t('f-supp-order-symp')}:`} />
                          <ListItemText className={classes.mxWidthWordBreak} primary={item.details.symptoms ?? '-'} />
                        </ListItem>
                        <ListItem className={classes.column} alignItems="flex-start">
                          <ListItemText secondary={`${t('f-supp-order-diag')}:`} />
                          <ListItemText className={classes.mxWidthWordBreak} primary={item.details.diagnosis ?? '-'} />
                        </ListItem>
                      </List>
                    </TableCell>

                    <TableCell style={{ padding: 0 }}>
                      <List dense={true} className={classes.vAlignTop}>
                        <ListItem className={classes.column} alignItems="flex-start">
                          <ListItemText secondary={`${t('f-animal-avg-weight')}:`} />
                          <ListItemText
                            primary={item.details.averageWeight > 0 ? `${item.details.averageWeight} kg` : '-'}
                          />
                        </ListItem>
                        <ListItem className={classes.column} alignItems="flex-start" style={{ minWidth: 175 }}>
                          <ListItemText secondary={`${t('f-animal-total-weight')}:`} />
                          <ListItemText
                            primary={
                              item.details.averageWeight && item.details.animalAmount
                                ? `${item.details.averageWeight * item.details.animalAmount} kg`
                                : '-'
                            }
                          />
                        </ListItem>
                      </List>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  // row render func
  return (
    <React.Fragment key={order.id}>
      <TableRow key={`btn${order.id}`} hover selected={rowExpanded}>
        <TableCell>
          <Tooltip arrow title={rowExpanded ? t('f-supp-order-hide') : t('f-supp-order-expand')}>
            <IconButton aria-label="expand row" size="small" onClick={() => setRowExpanded(!rowExpanded)}>
              {rowExpanded ? <Icon>{'keyboard_arrow_up'}</Icon> : <Icon>{'keyboard_arrow_down'}</Icon>}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>{`#${order.number}`}</TableCell>
        {mode === 'veterinary' && <TableCell>{order.farmName}</TableCell>}
        <TableCell>{SupplyOrderStatusBadge(order)}</TableCell>
        <TableCell>{new Date(order.created).toLocaleString()}</TableCell>
        <TableCell>{order.completed ? new Date(order.completed).toLocaleString() : '-'}</TableCell>
        <TableCell>{actionCellComponent}</TableCell>
      </TableRow>

      <TableRow key={`row${order.id}`}>
        <TableCell className={classes.tableRowArrowCell} colSpan={11}>
          <Collapse in={rowExpanded} timeout="auto" unmountOnExit style={{ paddingTop: '32px', paddingBottom: '32px' }}>
            {renderOrderDetailsSection(order)}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export const SupplyCell = (item: MedicalSupplyOrderItemViewModel, order: MedicalSupplyOrderViewModel) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const medSup = item.medicalSupplyBottle.medicalSupply;
  const medBottle = item.medicalSupplyBottle;

  const SupplyTypeAvatar = (type: MedicalSupplyType) => {
    const title = MedicalSupplyType[type] || '-';
    return (
      <Tooltip arrow title={title} placement="left">
        <Avatar variant="rounded" className={classes.smallAvatar}>
          {title[0]}
        </Avatar>
      </Tooltip>
    );
  };

  const SupplyMethodChip = (type: MedicalSupplyMethod) => {
    const method =
      type === MedicalSupplyMethod.Intermuscular ? t('f-supplies-method-muscular') : t('f-supplies-method-oral');

    return (
      <Tooltip arrow title={method} placement="left">
        <Avatar variant="rounded" className={classes.smallAvatar}>
          {method[0]}
        </Avatar>
      </Tooltip>
    );
  };

  // render
  return (
    <Box minWidth={175}>
      <Grid container direction="column" alignItems="center" spacing={1}>
        <Grid item container direction="row" spacing={1} alignItems="center">
          <Grid item xs={2}>
            {order.status === MedicalSupplyOrderStatus.Processing ? `${item.amount}x` : `${item.realAmount}x`}
          </Grid>
          <Grid item>
            <Chip
              label={`${item.medicalSupplyBottle.medicalSupply.number} ${item.medicalSupplyBottle.size}`}
              size="small"
              variant="outlined"
            />
          </Grid>
          {item.details.recurrentTreatment && (
            <Grid item>
              <Tooltip arrow title={t('f-supp-order-recur-treat')}>
                <Icon fontSize="small">{'sync'}</Icon>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <Grid item container direction="row" spacing={1} alignItems="center">
          <Grid item xs={2}></Grid>
          <Grid item>{SupplyTypeAvatar(item.medicalSupplyBottle.medicalSupply.supplyType)}</Grid>
          <Grid item>{SupplyMethodChip(medSup.method)}</Grid>
        </Grid>

        <Grid item container direction="row" spacing={1} alignItems="center">
          <Grid item xs={2}>
            {item.details.animalAmount}x
          </Grid>
          <Grid item>
            <Chip label={AnimalType[item.medicalSupplyBottle.animalType]} size="small" variant="outlined" />
          </Grid>
        </Grid>

        <Grid item container direction="row" spacing={1}>
          {medBottle.kgPerOneCC && (
            <Grid item container direction="row" spacing={1}>
              <Box mt={1}>
                <Typography variant={'body2'}>{t('f-kg-per-one-cc-var', { kg: medBottle.kgPerOneCC })}</Typography>
              </Box>
            </Grid>
          )}

          {medBottle.ccPerDose && (
            <Grid item container direction="row" spacing={1}>
              <Box mt={1}>
                <Typography variant={'body2'}>{t('f-cc-per-dose-var', { cc: medBottle.ccPerDose })}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export const SupplyOrderStatusBadge = (order: MedicalSupplyOrderViewModel) => {
  const classes = useStyles();
  let chipClass = classes.blueChip; // Accepted
  if (order.status === MedicalSupplyOrderStatus.Completed) chipClass = classes.greenChip;
  if (order.status === MedicalSupplyOrderStatus.Processing) chipClass = classes.orangeChip;

  return <Chip size="small" variant="outlined" label={MedicalSupplyOrderStatus[order.status]} className={chipClass} />;
};

export const RecurringTreatmentIcon = () => {
  const { t } = useTranslation();

  return (
    <Tooltip arrow title={t('f-supp-order-recur-treat')}>
      <Icon fontSize="small">{'sync'}</Icon>
    </Tooltip>
  );
};

export const SupplyAmountCell = (item: MedicalSupplyOrderItemViewModel, order: MedicalSupplyOrderViewModel) => {
  return order.status === MedicalSupplyOrderStatus.Processing ? `${item.amount}x` : `${item.realAmount}x`;
};
